import { createContext, use, useMemo, useState, type ReactNode } from 'react'
import type { RedeemTarget } from '../components/redeem-target'

/** Ephemeral state for the current till session (cleared between customers). */
interface SessionValue {
  scannedQr: string | null
  /** Set the scanned QR (starts a new customer session). */
  setScannedQr: (value: string | null) => void
  /** Reward ids claimed this session — shown applied (green) in the available list. */
  redeemedRewardIds: string[]
  /** Where each claimed reward's discount was applied (line / whole sale), so Annuler can toggle
   * the exact same target back off. Lives in the session so it survives the reward animation. */
  appliedTargets: Record<string, RedeemTarget>
  /** Mark a reward as claimed (remembering where its discount landed). `target` is optional only
   * for seeding a legacy ref that carried no target — a freshly applied reward always has one. */
  markRedeemed: (rewardId: string, target?: RedeemTarget) => void
  /** Undo a claim (the cashier cancelled the applied reward). */
  unmarkRedeemed: (rewardId: string) => void
  reset: () => void
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [scannedQr, setScannedQrState] = useState<string | null>(null)
  const [redeemedRewardIds, setRedeemedRewardIds] = useState<string[]>([])
  const [appliedTargets, setAppliedTargets] = useState<Record<string, RedeemTarget>>({})

  const value = useMemo<SessionValue>(
    () => ({
      scannedQr,
      setScannedQr: (value) => {
        setScannedQrState(value)
        setRedeemedRewardIds([])
        setAppliedTargets({})
      },
      redeemedRewardIds,
      appliedTargets,
      markRedeemed: (rewardId, target) => {
        setRedeemedRewardIds((ids) => (ids.includes(rewardId) ? ids : [...ids, rewardId]))
        if (target) setAppliedTargets((t) => ({ ...t, [rewardId]: target }))
      },
      unmarkRedeemed: (rewardId) => {
        setRedeemedRewardIds((ids) => ids.filter((id) => id !== rewardId))
        setAppliedTargets((t) => {
          const next = { ...t }
          delete next[rewardId]
          return next
        })
      },
      reset: () => {
        setScannedQrState(null)
        setRedeemedRewardIds([])
        setAppliedTargets({})
      },
    }),
    [scannedQr, redeemedRewardIds, appliedTargets],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const ctx = use(SessionContext)
  if (!ctx) throw new Error('useSession must be used within a SessionProvider')
  return ctx
}
